import { useEffect, useRef } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { message } from "antd";

import {
  type SupportRequestAttachment,
  type SupportRequestEntity,
  type SupportRequestFixedInVersion,
  type SupportRequestStatus,
} from "@fitness-app/data-models";
import { getErrorMessage } from "@fitness-app/utils";

import { getClientWebApiBaseUrl } from "~/modules/Sales/SalesAgent/constants";
import { supabase } from "~/store/initializeStore";
import {
  SUPPORT_COMMENTS_QUERY_KEY,
  SUPPORT_REQUESTS_QUERY_KEY,
  type SupportRequestWithRelations,
} from "../supportConstants";

// Writes that mirror to Linear go through client-web /api/support/* (the Linear API key
// lives server-side). Reads and comments use supabase directly (RLS).
export const callSupportApi = async <T>(path: string, body: Record<string, unknown>): Promise<T> => {
  const token = (await supabase.auth.getSession()).data.session?.access_token;

  if (!token) {
    throw new Error("Brak aktywnej sesji.");
  }

  const response = await fetch(`${getClientWebApiBaseUrl()}/api/support/${path}`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(body),
  });

  const payload = (await response.json()) as { status: string; data?: T; error?: string };

  if (!response.ok || payload.status !== "success") {
    throw new Error(payload.error || `Request failed with status ${response.status}`);
  }

  return payload.data as T;
};

export interface CreateSupportRequestPayload {
  title: string;
  description?: string;
  traineeId?: string;
  clientEmail?: string;
  assigneeUserId?: string;
  // Predefined values only — trainer_config.supportRequestTags.
  tags?: string[];
  attachments?: { name: string; url: string; mimeType?: string }[];
  // Source relation, e.g. the chat message the ticket was created from.
  metadata?: { chatMessageId?: string; chatChannelId?: string };
}

export const useCreateSupportRequest = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: CreateSupportRequestPayload) =>
      callSupportApi<{ request: SupportRequestEntity; linearSynced: boolean }>("create-request", { ...payload }),
    onSuccess: (data) => {
      void queryClient.invalidateQueries({ queryKey: [SUPPORT_REQUESTS_QUERY_KEY] });
      message.success(
        data.linearSynced && data.request.linearIssueIdentifier
          ? `Zgłoszenie dodane (${data.request.linearIssueIdentifier})`
          : "Zgłoszenie dodane",
      );
    },
    onError: (error) => {
      message.error(getErrorMessage(error));
    },
  });
};

export interface UpdateSupportRequestPayload {
  id: string;
  assigneeUserId?: string | null;
  // Owner-level only (enforced server-side): reassign the report to the team
  // member it actually came from.
  reporterUserId?: string;
  // The client the ticket is about — null unlinks. Board-wide roles only
  // (enforced server-side); linking a client also syncs clientEmail to theirs.
  traineeId?: string | null;
  clientEmail?: string | null;
  dueDate?: string | null;
  // Null clears the description (drawer edit — TRE-1558).
  description?: string | null;
  // Full replacement list, predefined values only (trainer_config.supportRequestTags).
  tags?: string[];
  // Full replacement list — releases the fix shipped in (options come from app_release).
  fixedInVersions?: SupportRequestFixedInVersion[];
  // Full replacement list — the drawer sends existing + newly uploaded.
  attachments?: SupportRequestAttachment[];
}

export const useUpdateSupportRequest = () => {
  const queryClient = useQueryClient();
  // Sequence of the latest optimistic write (tags/description) per request — a failed
  // OLDER mutation must not roll back a value picked after it was sent.
  const optimisticWriteSeqByRequestId = useRef(new Map<string, number>());

  return useMutation({
    mutationFn: (payload: UpdateSupportRequestPayload) =>
      callSupportApi<{ updated: boolean; assigneeUserId: string | null; dueDate: string | null }>("update-request", {
        ...payload,
      }),
    // Optimistic tags/description/fixedInVersions/traineeId: the drawer reads its
    // values from this cache, so without an immediate write the change only shows up
    // after the round trip (and disabling the field meanwhile felt broken). Same
    // pattern as useUpdateSupportStatus. Only the traineeId column is mirrored, not
    // the joined trainee record — the drawer waits for the refetch before trusting
    // that relation again.
    onMutate: ({ id, tags, description, fixedInVersions, traineeId }) => {
      if (tags === undefined && description === undefined && fixedInVersions === undefined && traineeId === undefined) {
        return {};
      }

      void queryClient.cancelQueries({ queryKey: [SUPPORT_REQUESTS_QUERY_KEY] });

      const seq = (optimisticWriteSeqByRequestId.current.get(id) ?? 0) + 1;
      optimisticWriteSeqByRequestId.current.set(id, seq);

      const previousRequest = queryClient
        .getQueryData<SupportRequestWithRelations[]>([SUPPORT_REQUESTS_QUERY_KEY])
        ?.find((request) => request.id === id);
      const previousTags = previousRequest?.tags;
      const previousDescription = previousRequest?.description;
      const previousFixedInVersions = previousRequest?.fixedInVersions;
      const previousTraineeId = previousRequest?.traineeId;

      queryClient.setQueryData<SupportRequestWithRelations[]>([SUPPORT_REQUESTS_QUERY_KEY], (current) =>
        (current || []).map((request) =>
          request.id === id
            ? {
                ...request,
                ...(tags !== undefined ? { tags } : {}),
                ...(description !== undefined ? { description } : {}),
                ...(fixedInVersions !== undefined ? { fixedInVersions } : {}),
                ...(traineeId !== undefined ? { traineeId } : {}),
              }
            : request,
        ),
      );

      return { seq, previousTags, previousDescription, previousFixedInVersions, previousTraineeId };
    },
    onError: (error, { id, tags, description, fixedInVersions, traineeId }, context) => {
      // Roll back only the fields this mutation wrote, and only when no newer
      // optimistic edit landed meanwhile — otherwise the newer optimistic value
      // stays and the onSettled refetch reconciles with the server.
      if (context?.seq !== undefined && context.seq === optimisticWriteSeqByRequestId.current.get(id)) {
        queryClient.setQueryData<SupportRequestWithRelations[]>([SUPPORT_REQUESTS_QUERY_KEY], (current) =>
          (current || []).map((request) =>
            request.id === id
              ? {
                  ...request,
                  ...(tags !== undefined ? { tags: context.previousTags ?? [] } : {}),
                  ...(description !== undefined ? { description: context.previousDescription ?? null } : {}),
                  ...(fixedInVersions !== undefined ? { fixedInVersions: context.previousFixedInVersions ?? [] } : {}),
                  ...(traineeId !== undefined ? { traineeId: context.previousTraineeId ?? null } : {}),
                }
              : request,
          ),
        );
      }
      message.error(getErrorMessage(error));
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: [SUPPORT_REQUESTS_QUERY_KEY] });
    },
  });
};

// Tags / fixedInVersions are full-replacement lists edited with rapid multi-select
// clicks. Firing one update per click raced: two in-flight replacements could settle
// out of order and the slower (stale) one won, dropping just-picked values. This
// wrapper keeps the cache write immediate (the selects read from it), debounces the
// server call, and never keeps more than one write in flight per request — a list
// picked while a write is flying is coalesced and sent after it settles.
type CoalescedFieldPatch = Pick<UpdateSupportRequestPayload, "tags" | "fixedInVersions">;

interface CoalescedFieldState {
  timer?: ReturnType<typeof setTimeout>;
  // Latest not-yet-sent lists; tags and fixedInVersions merge so editing one field
  // during the other's debounce window loses neither.
  pendingPatch?: CoalescedFieldPatch;
  inFlight: boolean;
}

export const useCoalescedSupportRequestFieldUpdate = (delayMs = 600) => {
  const queryClient = useQueryClient();
  const updateRequest = useUpdateSupportRequest();
  const stateByRequestId = useRef(new Map<string, CoalescedFieldState>());

  const getState = (id: string): CoalescedFieldState => {
    const existing = stateByRequestId.current.get(id);
    if (existing) return existing;
    const created: CoalescedFieldState = { inFlight: false };
    stateByRequestId.current.set(id, created);
    return created;
  };

  const send = (id: string, patch: CoalescedFieldPatch) => {
    const state = getState(id);
    state.inFlight = true;
    // mutateAsync, not mutate-with-callbacks: per-call mutate callbacks are dropped
    // when the component unmounts mid-flight, which would strand a queued patch. The
    // promise chain settles regardless of the React lifecycle, so the queue always
    // drains. Errors are already surfaced by the hook-level onError.
    void updateRequest
      .mutateAsync({ id, ...patch })
      .catch(() => undefined)
      .finally(() => {
        state.inFlight = false;
        const queued = state.pendingPatch;
        if (queued && !state.timer) {
          state.pendingPatch = undefined;
          // The hook-level invalidate fired on settle, but this follow-up mutation's
          // onMutate cancels that refetch before a stale response can clobber the cache.
          send(id, queued);
        } else if (!queued && !state.timer) {
          stateByRequestId.current.delete(id);
        }
      });
  };

  const flush = (id: string, state: CoalescedFieldState) => {
    if (state.timer) {
      clearTimeout(state.timer);
      state.timer = undefined;
    }
    if (state.pendingPatch && !state.inFlight) {
      const patch = state.pendingPatch;
      state.pendingPatch = undefined;
      send(id, patch);
    }
  };

  const queueFieldUpdate = (id: string, patch: CoalescedFieldPatch) => {
    // The mutation is still hundreds of ms away — an in-flight refetch (another
    // mutation settling, window refocus) landing inside the debounce window would
    // overwrite the optimistic list and the next click would build on a stale value.
    void queryClient.cancelQueries({ queryKey: [SUPPORT_REQUESTS_QUERY_KEY] });

    // Immediate cache write — same shape as the mutation's onMutate, so the select
    // (which reads from the cache) updates on every click, not after the debounce.
    queryClient.setQueryData<SupportRequestWithRelations[]>([SUPPORT_REQUESTS_QUERY_KEY], (current) =>
      (current || []).map((request) => (request.id === id ? { ...request, ...patch } : request)),
    );

    const state = getState(id);
    state.pendingPatch = { ...state.pendingPatch, ...patch };
    if (state.timer) clearTimeout(state.timer);
    state.timer = setTimeout(() => {
      state.timer = undefined;
      if (state.inFlight || !state.pendingPatch) return;
      const pending = state.pendingPatch;
      state.pendingPatch = undefined;
      send(id, pending);
    }, delayMs);
  };

  // A click right before navigating away must still reach the server. (A patch queued
  // behind an in-flight write can't be flushed here — its chained onSettled callback
  // does not run after unmount — but that window is a fraction of a second.)
  useEffect(() => {
    const states = stateByRequestId.current;
    return () => {
      states.forEach((state, id) => flush(id, state));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { queueFieldUpdate };
};

// Manual Linear mirror for a request created while the sync was down (TRE-1552).
// Owner-level only — enforced server-side, the drawer additionally hides the button.
export const useSyncRequestToLinear = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id }: { id: string }) =>
      callSupportApi<{ synced: boolean; linearIssueIdentifier: string; linearIssueUrl: string }>("sync-linear", {
        id,
      }),
    onSuccess: (data) => {
      void queryClient.invalidateQueries({ queryKey: [SUPPORT_REQUESTS_QUERY_KEY] });
      message.success(`Utworzono issue w Linear (${data.linearIssueIdentifier})`);
    },
    onError: (error) => {
      message.error(getErrorMessage(error));
    },
  });
};

export const useDeleteSupportRequest = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id }: { id: string }) => callSupportApi<{ deleted: boolean }>("delete-request", { id }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [SUPPORT_REQUESTS_QUERY_KEY] });
      message.success("Zgłoszenie usunięte");
    },
    onError: (error) => {
      message.error(getErrorMessage(error));
    },
  });
};

export const useResolveTriage = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, outcome }: { id: string; outcome: "approved" | "rejected" }) =>
      callSupportApi<{ updated: boolean; status: SupportRequestStatus; linearSynced: boolean }>("resolve-triage", {
        id,
        outcome,
      }),
    onSuccess: (data, { outcome }) => {
      void queryClient.invalidateQueries({ queryKey: [SUPPORT_REQUESTS_QUERY_KEY] });
      // updated=false = someone else resolved the proposal first — don't fake a success.
      if (data.updated) {
        message.success(outcome === "approved" ? "Propozycja agenta zatwierdzona" : "Propozycja agenta odrzucona");
      } else {
        message.info("Propozycja została już rozstrzygnięta przez kogoś innego");
      }
    },
    onError: (error) => {
      message.error(getErrorMessage(error));
    },
  });
};

// Statuses of in-flight status mutations, layered over every fetch result by
// useSupportRequests' select. The update-status endpoint syncs Slack + Linear before
// responding (seconds on prod), and any refetch resolving in that window — window
// focus, another mutation's invalidate — would snap the dragged card back to its old
// column until onSettled's refetch lands (TRE-1765 #1).
export const pendingStatusOverrides = new Map<string, SupportRequestStatus>();

export const useUpdateSupportStatus = () => {
  const queryClient = useQueryClient();

  return useMutation({
    // comment: optional closing note (Done/Rejected modal) — the endpoint stores it
    // as a comment linked to the transition and quotes it in the Slack thread.
    mutationFn: ({ id, status, comment }: { id: string; status: SupportRequestStatus; comment?: string }) =>
      callSupportApi<{ updated: boolean; linearSynced: boolean }>("update-status", { id, status, comment }),
    onMutate: ({ id, status }) => {
      // setQueriesData must run synchronously — awaiting cancelQueries first lets the
      // dropped card render in its old column for a frame (visible flicker on dnd).
      void queryClient.cancelQueries({ queryKey: [SUPPORT_REQUESTS_QUERY_KEY] });

      pendingStatusOverrides.set(id, status);
      // Prefix match covers every cache built on the key — the board list and the
      // per-trainee lists ([key, "trainee", traineeId]) used by the profile tab.
      const previous = queryClient.getQueriesData<SupportRequestWithRelations[]>({
        queryKey: [SUPPORT_REQUESTS_QUERY_KEY],
      });

      queryClient.setQueriesData<SupportRequestWithRelations[]>({ queryKey: [SUPPORT_REQUESTS_QUERY_KEY] }, (current) =>
        (current || []).map((request) => (request.id === id ? { ...request, status } : request)),
      );

      return { previous };
    },
    onError: (error, variables, context) => {
      pendingStatusOverrides.delete(variables.id);
      for (const [queryKey, data] of context?.previous || []) {
        queryClient.setQueryData(queryKey, data);
      }
      message.error(`Nie udało się zmienić statusu: ${getErrorMessage(error)}`);
    },
    onSettled: (_data, _error, variables) => {
      // Clear before the refetch below — after success the server already has the new
      // status, after error the rollback above must stay visible.
      pendingStatusOverrides.delete(variables.id);
      void queryClient.invalidateQueries({ queryKey: [SUPPORT_REQUESTS_QUERY_KEY] });
      if (variables.comment) {
        void queryClient.invalidateQueries({ queryKey: [SUPPORT_COMMENTS_QUERY_KEY, variables.id] });
      }
    },
  });
};
